import { useState } from 'react'
import { useQuoteStore } from '../store'
import { useScopeHours } from '../../scope/useScopeHours'
import { useHomeStore } from '@/features/home/store'
import { useAuthStore } from '@store/authStore'
import { quotesApi, dealsApi, getAuthHeaders } from '@api/client'
import { mapStoreStateToDbQuote } from '../utils/mappers'
import { showToast } from '@store/uiStore'

const API_BASE = '/api'

export interface SubmitForReviewResult {
  ok: boolean
  /** True when the server diverted the quote to the Implementations team (PR 2). */
  diverted: boolean
}

/**
 * Shared standard-review submission. Saves the quote, flips the deal to `in_review`,
 * and posts the Slack review request. The server (PR 2) decides whether to divert to
 * the Implementations team; when it does, it resets the deal back to `draft` and the
 * response carries `implReview: true`, surfaced here as `diverted` so the caller can
 * re-fetch the deal (banner → awaiting). Used by QuoteHeader's submit button and the
 * emerald-banner "continue" CTA.
 *
 * Extracted verbatim from QuoteHeader.handleSubmitForReview so both entry points share
 * one implementation. The advisory `needsImplReview` hint is recomputed here; the server
 * re-derives it authoritatively.
 */
export function useSubmitForReview() {
  const { details, selectedProducts, partnership, calculations } = useQuoteStore()
  const { totalHours: scopeTotalHours } = useScopeHours()
  const { selectedDeal } = useHomeStore()
  const { user } = useAuthStore()
  const [isSubmitting, setIsSubmitting] = useState(false)

  const submit = async (dealId: number): Promise<SubmitForReviewResult> => {
    const scopeFee = scopeTotalHours * details.hourlyRate
    const feeFromScope = details.implementationFeeFromScope
    const feeStale = feeFromScope && Math.abs(details.implementationFee - scopeFee) > 0.5
    const feeSyncedWithScope = feeFromScope && !feeStale
    const needsSync = scopeTotalHours > 0 && !feeSyncedWithScope

    setIsSubmitting(true)
    try {
      const dbQuote = mapStoreStateToDbQuote({ details, selectedProducts, partnership, calculations })
      await quotesApi.save(dealId, dbQuote)

      // Notify Slack FIRST, then flip the deal into review only on a successful standard
      // post (below). This way a Slack failure can't leave a deal queued with no channel
      // message — the deal stays a draft and the AE can retry. (The server's divert path
      // sets the 'awaiting' state itself and returns implReview:true.)
      const baseUrl = window.location.origin
      const quoteUrl = `${baseUrl}/quote?session=${dealId}`
      const discountedProducts = selectedProducts.filter((p) => p.discountType !== 'none')

      const response = await fetch(`${API_BASE}/slack/review`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({
          customerName: details.customerName,
          preparedBy: details.preparedBy,
          plan: details.customerPlan,
          contractTerm: details.contractTermMonths,
          currency: details.displayCurrency,
          paymentFrequency: details.paymentFrequency,
          arr: calculations?.arr || 0,
          tcv: calculations?.tcv || 0,
          grossProfitMargin: calculations?.grossProfitMargin || 0,
          ltvCacRatio: calculations?.ltvCacRatio || 0,
          overallAssessment: calculations?.overallRating || { rating: 'N/A', score: 0 },
          productCount: selectedProducts.length,
          platformFees: details.platformFees,
          implementationFee: details.implementationFee,
          // Reviewer signal: is the implementation fee sourced from the scope?
          implementationFeeScopeStatus: scopeTotalHours <= 0
            ? 'no_scope'
            : feeSyncedWithScope
              ? 'synced'
              : feeStale
                ? 'stale'
                : 'not_sourced',
          scopeTotalHours,
          implementationScopeHours: details.implementationScopeHours,
          minimumCommitment: details.minimumCommitment,
          products: selectedProducts.slice(0, 5).map((p) => ({
            name: p.name,
            volume: p.volume,
            effectivePrice: p.effectivePrice,
            discountType: p.discountType,
            discountValue: p.discountValue,
          })),
          totalProducts: selectedProducts.length,
          discountedProducts: discountedProducts.length,
          hubspotDealId: selectedDeal?.id,
          hubspotDealName: selectedDeal?.name,
          quoteUrl,
          scopeUrl: `${baseUrl}/scope?session=${details.scopeSourceDealId || dealId}`,
          scopeSourceDealId: details.scopeSourceDealId || null,
          submittedByEmail: user?.email,
          submittedByName: user?.name,
          // The server runs the authoritative sync check; these are advisory (PR 2).
          dealId,
          needsImplReview: needsSync,
        }),
      })

      if (!response.ok) {
        const error = await response.json().catch(() => ({} as { error?: string }))
        throw new Error(error.error || 'Failed to notify Slack — the quote was not submitted. Please try again.')
      }

      // PR 2's divert path returns { implReview: true } and has already set the deal to
      // 'awaiting' server-side — nothing more to do here.
      const result = await response.json().catch(() => ({} as { implReview?: boolean }))
      if (result?.implReview === true) {
        showToast('Sent to the Implementations team — Alitia and Aju have been notified in Slack.', 'success', 5000)
        return { ok: true, diverted: true }
      }

      // Standard path: Slack notified the reviewers, so now flip the deal into review.
      const updateResult = await dealsApi.update(dealId, {
        status: 'in_review',
        review_submitted_by_email: user?.email,
        review_submitted_by_name: user?.name,
      } as any)
      if (!updateResult.success) {
        if (updateResult.error?.includes('finalized')) {
          showToast('This deal has been finalized and cannot be re-submitted for review. Please contact the reviewer to re-open it.', 'error', 8000)
        } else {
          showToast(updateResult.error || 'Notified Slack, but could not mark the deal as in review. Please try again.', 'error')
        }
        return { ok: false, diverted: false }
      }

      showToast('Quote submitted for review! Notification sent to Slack.', 'success', 5000)
      return { ok: true, diverted: false }
    } catch (error) {
      console.error('Submit for review error:', error)
      showToast(error instanceof Error ? error.message : 'Failed to submit for review. Please try again.', 'error')
      return { ok: false, diverted: false }
    } finally {
      setIsSubmitting(false)
    }
  }

  return { submit, isSubmitting }
}
